// src/components/approval/ApprovalSaveModal.jsx
import React, { useState } from "react";

const ApprovalSaveModal = ({
  isOpen,
  onClose,
  onSave,
  approvalLines,
  users,
  // 선택: 바깥 form을 submit 시키고 싶다면 formId를 받아서 버튼에 form 속성 부여 가능
  formId, // ex) "approval-form"
}) => {
  const [formData, setFormData] = useState({
    name: "",
  });

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      alert("결재선 이름을 입력해주세요.");
      return;
    }

    if (approvalLines.length === 0) {
      alert("저장할 결재선이 없습니다.");
      return;
    }

    const template = {
      id: Date.now(),
      name: formData.name,
      approvalLines: [...approvalLines],
      createdAt: new Date().toISOString(),
    };

    onSave(template);
    handleClose();
  };

  const handleClose = () => {
    setFormData({ name: "" });
    onClose();
  };

  const getApproverName = (approverId) => {
    const user = users.find((u) => String(u.id) === String(approverId));
    return user ? user.last_name + user.first_name : "결재자 미선택";
  };

  const renderApprovalLines = () => {
    // 병렬결재 그룹화
    const orderGroups = {};
    approvalLines.forEach((line) => {
      const order = line.order;
      if (!orderGroups[order]) {
        orderGroups[order] = [];
      }
      orderGroups[order].push(line);
    });

    const groupedLines = Object.keys(orderGroups)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map((order) => orderGroups[order]);

    return groupedLines.map((group, groupIndex) => {
      const isParallelGroup = group.length > 1;
      const groupOrder = group[0].order;

      if (isParallelGroup) {
        return (
          <div key={`group-${groupIndex}`} className="mb-2">
            <div className="flex items-center space-x-2 mb-1">
              <span className="text-sm font-medium text-gray-600 w-6 text-center">
                {groupOrder}
              </span>
              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                병렬결재 ({group.length}명)
              </span>
            </div>
            <div className="ml-6 space-y-1">
              {group.map((line) => (
                <div key={line.id} className="text-sm text-gray-700">
                  • {getApproverName(line.approver_id)} (
                  {line.approval_category === "pre_approval" ? "전결" : "결재"})
                </div>
              ))}
            </div>
          </div>
        );
      } else {
        const line = group[0];
        return (
          <div key={line.id} className="flex items-center space-x-2 mb-2">
            <span className="text-sm font-medium text-gray-600 w-6 text-center">
              {groupOrder}
            </span>
            <span className="text-sm text-gray-700">
              {getApproverName(line.approver_id)} (
              {line.approval_category === "pre_approval" ? "전결" : "결재"})
            </span>
          </div>
        );
      }
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* 헤더 */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">결재선 저장</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* 내용 */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* ⛔️ form 제거: 중첩 <form> 방지 */}
          <div className="space-y-6">
            {/* 입력 폼 */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  결재선 이름 *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="결재선 이름을 입력하세요"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            {/* 저장할 결재선 미리보기 */}
            <div>
              <h3 className="text-lg font-medium mb-3">저장할 결재선</h3>
              {approvalLines.length === 0 ? (
                <div className="text-center py-8 border border-gray-200 rounded-md bg-gray-50">
                  <p className="text-gray-500">저장할 결재선이 없습니다.</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                  {renderApprovalLines()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 푸터 */}
        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            취소
          </button>

          {/* 모달 자체 저장(템플릿 저장) */}
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            저장
          </button>

          {/* 선택: 바깥 결재 상신 폼 제출 버튼(원하면 표시) */}
          {formId && (
            <button
              type="submit"
              form={formId} // ex) "approval-form"
              className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              title="바깥 결재 상신 폼 제출"
            >
              상신 제출
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApprovalSaveModal;
